import { useEffect, useState } from 'react';
import { CardTapPage, withCustomRender } from 'airkey-mfa-react';
import { getSession, rejectTx, validateTap } from '@/api/bff';
import { FullScreenLoader } from '@/components/LoadScreen/FullScreenLoader';
import { SandboxCancelledScreen } from '@/components/Sandbox/SandboxCancelledScreen/SandboxCancelledScreen';
import { SandboxMessageScreen } from '@/components/Sandbox/SandboxMessageScreen/SandboxMessageScreen';
import { SandboxSuccessScreen } from '@/components/Sandbox/SandboxSuccessScreen/SandboxSuccessScreen';
import { WEB_NFC_PERMISSIONS_VIDEO } from '@/components/3ds/TapCardPage/model/constants';
import { getErrorType } from '@/utils/errors';
import { CustomTapRender } from '../CustomTapRender/CustomTapRender';

const INTENT_OWNER = 'sb';
const WORKFLOW = 'auth';
const REDIRECT_URL =
  import.meta.env.MODE === 'production'
    ? 'https://developer.airkey.com'
    : 'https://developer.dev-airkey.com';

export const TapScreen = ({ processId }: { processId: string }) => {
  const [isLoading, setLoading] = useState(true);
  const [nonce, setNonce] = useState<string | undefined>();
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>();
  const [isCancelled, setIsCancelled] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  useEffect(() => {
    const getTransaction = async () => {
      try {
        const session = await getSession(INTENT_OWNER, WORKFLOW, processId);
        setNonce(session.nonce);
        setRedirectUrl(session.redirectUrl ?? REDIRECT_URL);
      } catch {
        setNetworkError(true);
      } finally {
        setLoading(false);
      }
    };
    getTransaction();
  }, [processId]);

  if (isLoading) return <FullScreenLoader />;

  if (networkError) {
    return (
      <SandboxMessageScreen
        title="Error"
        body="A network error has occurred. Please try again."
      />
    );
  }

  if (verificationFailed) {
    return (
      <SandboxMessageScreen
        title="Verification Failed"
        body="You have attempted to complete AirKey verification using a card or device that is not paired to your account."
      />
    );
  }

  if (isCancelled) return <SandboxCancelledScreen redirectUrl={redirectUrl} />;
  if (isSuccess) return <SandboxSuccessScreen redirectUrl={redirectUrl} />;

  const AirKeyUi = withCustomRender(CardTapPage, CustomTapRender);

  const handleClose = async () => {
    try {
      await rejectTx(processId, INTENT_OWNER, WORKFLOW);
      setIsCancelled(true);
    } catch {
      setNetworkError(true);
    }
  };

  const handleRead = async (airKeyMessage: string) => {
    try {
      console.log('airkeyMessage, ', airKeyMessage, 'length: ', airKeyMessage.length);
      const { validationResult } = await validateTap(
        processId,
        airKeyMessage,
        INTENT_OWNER,
        WORKFLOW,
      );

      if (validationResult === 'Success') {
        setIsSuccess(true);
        return;
      }
      if (validationResult === 'Failure') {
        setVerificationFailed(true);
      }
    } catch (error) {
      const errorType = getErrorType(error);
      if (errorType === 'noActiveRequests') {
        setVerificationFailed(true);
      } else {
        setNetworkError(true);
      }
    }
  };

  return (
    <AirKeyUi
      nonce={nonce}
      onCardTapResult={handleRead}
      close={handleClose}
      webNfcPermissionsVideo={WEB_NFC_PERMISSIONS_VIDEO}
    />
  );
};
